/**
 * ThemeSwitcher — the tri-state Light / Dark / System control in the header.
 *
 * A SEGMENTED control implemented as a WAI-ARIA radio group (`role="radiogroup"`
 * with `role="radio"` children): roving tabindex (the checked option is the sole
 * tab stop), Arrow keys move + select within the group, Space/Enter selects the
 * focused option. The active state is carried by MULTIPLE channels, never colour
 * alone (DESIGN.md "never" list): a raised card thumb (bg + shadow + hairline),
 * a heavier `--ink` label, and `aria-checked`.
 *
 * Below ~40rem the labels collapse to icon-only (label text → visually hidden,
 * `aria-label` + `title` preserved) — the CSS handles that; the accessible name
 * always stays the full word.
 *
 * Behaviour lives in lib/theme.ts (resolve + apply + persist). In "system" mode
 * we attach a `prefers-color-scheme` listener so an OS theme flip live-updates
 * the resolved `color-scheme` + `theme-color` without a reload (the tokens
 * themselves follow the scoped @media rules for free — see theme.ts mechanism).
 */

import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { applyTheme, persistTheme, readStoredTheme, type ThemeChoice } from "@/lib/theme";

interface ThemeOption {
  value: ThemeChoice;
  /** IT label; DE/EN/Ladin slot in when i18n lands (DESIGN.md). */
  label: string;
  icon: ReactNode;
}

/** 16px line icons, currentColor, decorative (labelled by the option text). */
const SunIcon = (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
    <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </g>
  </svg>
);

const MoonIcon = (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
    <path
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z"
    />
  </svg>
);

/** Split-disc: a circle half-filled — the "follow the system" glyph. */
const SystemIcon = (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
    <circle cx="12" cy="12" r="8.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <path fill="currentColor" d="M12 3.8a8.2 8.2 0 0 0 0 16.4V3.8Z" />
  </svg>
);

const OPTIONS: readonly ThemeOption[] = [
  { value: "light", label: "Chiaro", icon: SunIcon },
  { value: "dark", label: "Scuro", icon: MoonIcon },
  { value: "system", label: "Sistema", icon: SystemIcon },
];

export function ThemeSwitcher() {
  const [choice, setChoice] = useState<ThemeChoice>(() => readStoredTheme());
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Keep the current choice in a ref so the (once-attached) matchMedia listener
  // can read the latest value without re-subscribing on every change. Synced in an
  // effect (after render, not during) — the listener fires async on OS-scheme
  // change, so it always sees the latest committed choice.
  const choiceRef = useRef(choice);
  useEffect(() => {
    choiceRef.current = choice;
  });

  // Live-follow the OS scheme while in "system" mode. The tokens repaint via the
  // scoped @media rules on their own; this only re-applies color-scheme + the
  // theme-color meta so the browser chrome tracks the flip too.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      if (choiceRef.current === "system") applyTheme("system");
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Self-heal on mount: re-assert the resolved theme so React is authoritative
  // even if the pre-paint inline script is ever removed/reordered. In the normal
  // path the inline script already applied it before paint, so this is a no-op.
  useEffect(() => {
    applyTheme(readStoredTheme());
  }, []);

  function select(next: ThemeChoice) {
    setChoice(next);
    persistTheme(next);
    applyTheme(next);
  }

  function focusOption(index: number) {
    const wrapped = (index + OPTIONS.length) % OPTIONS.length;
    optionRefs.current[wrapped]?.focus();
    select(OPTIONS[wrapped]!.value);
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        focusOption(index + 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        focusOption(index - 1);
        break;
      case "Home":
        event.preventDefault();
        focusOption(0);
        break;
      case "End":
        event.preventDefault();
        focusOption(OPTIONS.length - 1);
        break;
      case " ":
      case "Enter":
        event.preventDefault();
        select(OPTIONS[index]!.value);
        break;
    }
  }

  return (
    <div className="theme-switcher" role="radiogroup" aria-label="Tema">
      {OPTIONS.map((option, index) => {
        const checked = option.value === choice;
        return (
          <button
            key={option.value}
            type="button"
            ref={(el) => {
              optionRefs.current[index] = el;
            }}
            className="theme-switcher__option"
            role="radio"
            aria-checked={checked}
            aria-label={option.label}
            title={option.label}
            tabIndex={checked ? 0 : -1}
            onClick={() => select(option.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {option.icon}
            <span className="theme-switcher__label">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
